<script setup lang="ts">
import { useLocations } from '@/composables/useLocations';
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

interface Location {
    id: number;
    city: string;
    address: string; // Changed from title to address
    code: string;
    value: string;
    destination_translations: string;
    language: string;
    country: string;
    isCurrent?: boolean;
    subtitle?: string;
    searchText?: string; // Combined searchable text
}

interface Props {
    label?: string;
    modelValue?: Location | null;
    showCurrentLocation?: boolean;
    isInDialog?: boolean;
    isLocationDialogOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    label: 'Select Location',
    modelValue: null,
    showCurrentLocation: true,
    isInDialog: false,
    isLocationDialogOpen: false,
});

const emit = defineEmits<{
    'update:modelValue': [value: Location | null];
    'location-selected': [location: Location];
}>();

const { locations } = useLocations();
const { locale } = useI18n();

// Mobile detection and menu state
const isMobile = ref(false);
const isMenuOpen = ref(false);
const currentCity = ref<string | null>(null);
const autocompleteRef = ref<any>(null);
const cardElement = ref<HTMLElement | null>(null);

// Function to check if screen is mobile (480px or below)
const checkMobile = () => {
    isMobile.value = window.innerWidth <= 480;
    // On mobile AND inside dialog, keep menu always open
    isMenuOpen.value = isMobile.value && props.isInDialog;
};

// Add event listener for window resize
onMounted(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    nextTick(() => {
        const el = autocompleteRef.value?.$el;
        if (el && typeof el.closest === 'function') {
            cardElement.value = el.closest('.v-card');
        }
    });
    try {
        if (props.showCurrentLocation) {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
            const rawCity = tz.split('/').pop() || '';
            const guessCity = rawCity.replace(/_/g, ' ').toLowerCase();
            currentCity.value = guessCity || null;
        }
    } catch (_) {
        currentCity.value = null;
    }
});

onUnmounted(() => {
    window.removeEventListener('resize', checkMobile);
});

const selectedLocation = computed({
    get: () => {
        if (!props.modelValue) return null;
        // Find the transformed location that matches the selected one
        const transformed = transformedLocations.value.find(loc => loc.code === props.modelValue!.code);
        return transformed || props.modelValue;
    },
    set: (value: Location | null) => {
        emit('update:modelValue', value);
    },
});

// Deduplicate locations by airport code, keeping the one with the most complete data
const deduplicatedLocations = computed(() => {
    const locationMap = new Map();

    locations.value.forEach((location: any) => {
        const existingLocation = locationMap.get(location.code);

        if (!existingLocation) {
            // First occurrence of this airport code
            locationMap.set(location.code, location);
        } else {
            // Check if current location has more complete data
            const currentHasTitle = location.address && location.address.trim() !== '';
            const existingHasTitle = existingLocation.address && existingLocation.address.trim() !== '';

            if (currentHasTitle && !existingHasTitle) {
                // Current location has title, existing doesn't - replace
                locationMap.set(location.code, location);
            } else if (currentHasTitle === existingHasTitle) {
                // Both have or don't have titles - keep the one with higher ID (more recent)
                if (location.id > existingLocation.id) {
                    locationMap.set(location.code, location);
                }
            }
        }
    });
    return Array.from(locationMap.values());
});

// Transform locations data to include subtitle, isCurrent flag, and searchable text
const transformedLocations = computed(() => {
    const selectedCode = props.modelValue?.code || null; // make selection reactive in this computed

    return deduplicatedLocations.value.map((location: any) => {
        const address = location.address ? location.address : location.city;
        const searchableFields = [
            location.city,
            location.address,
            location.code,
            location.country,
            location.destination_translations,
            location.language,
        ].filter(Boolean);

        // Mark current if it's the selected item OR matches timezone guess
        const isSelectedCurrent = !!props.showCurrentLocation && !!selectedCode && location.code === selectedCode;

        const isTimezoneCurrent =
            !!props.showCurrentLocation &&
            !!currentCity.value &&
            ((location.city && location.city.toLowerCase().includes(currentCity.value)) ||
                (address && address.toLowerCase().includes(currentCity.value)) ||
                (location.destination_translations && location.destination_translations.toLowerCase().includes(currentCity.value)) ||
                (currentCity.value === 'bucharest' && location.code === 'OTP'));

        const isCurrent = isSelectedCurrent || isTimezoneCurrent;

        return {
            ...location,
            address,
            subtitle: `${location.city}, ${location.country}`,
            isCurrent,
            searchText: searchableFields.join(' ').toLowerCase(),
        };
    });
});

// Separate current location and all locations
const currentLocation = computed(() => {
    return transformedLocations.value.filter((location: any) => location.isCurrent);
});

const allLocations = computed(() => {
    return transformedLocations.value.filter((location: any) => !location.isCurrent);
});

// Combined airports array for the autocomplete
const airports = computed(() => {
    return [...currentLocation.value, ...allLocations.value];
});

// Menu props with conditional attachment
const menuProps = computed(() => {
    const baseProps = {
        contentClass: 'fs-autocomplete-dropdown',
    };

    // On mobile and inside dialog, attach to the dialog container instead of body
    if (isMobile.value && props.isInDialog) {
        return {
            ...baseProps,
            attach: cardElement.value || '.v-dialog .v-card', // Attach to the specific dialog card
            location: 'bottom start',
            offset: 0,
        };
    }

    return baseProps;
});

// Custom filter function for comprehensive search
const customFilter = (value: string, query: string, item: any) => {
    if (!query) return true;

    const searchQuery = query.toLowerCase().trim();
    if (item.raw.searchText && item.raw.searchText.includes(searchQuery)) {
        return true;
    }

    // Also search in individual fields for more precise matching
    const searchableFields = [item.raw.city, item.raw.address, item.raw.code, item.raw.country, item.raw.destination_translations];
    return searchableFields.some((field) => field && field.toLowerCase().includes(searchQuery));
};

// Handle location selection
const onLocationSelect = (location: Location | null) => {
    selectedLocation.value = location;
    emit('update:modelValue', location);
    if (location) {
        emit('location-selected', location); // Only emission here
    }
};

const onMenuUpdate = (value: boolean) => {
    if (!isMobile.value || !props.isInDialog) {
        isMenuOpen.value = value;
        if (value) {
            nextTick(() => {
                setTimeout(() => {
                    const input = autocompleteRef.value?.$el?.querySelector('input');
                    if (input) {
                        input.focus();
                        input.select();
                    }
                }, 50); 
            });
        }
    }
};

// Watch for language changes and update selected location
watch(locale, () => {
    if (props.modelValue && props.modelValue.code) {
        // Find the corresponding location in the new language data
        const matchingLocation = locations.value.find((location: any) => location.code === props.modelValue!.code);

        if (matchingLocation) {
            // Update the selected location with the new language data
            emit('update:modelValue', matchingLocation);
        }
    }
});

// Watch for changes in isInDialog prop
watch(
    () => props.isInDialog,
    () => {
        checkMobile();
    },
);

watch(
    () => props.isLocationDialogOpen,
    (open) => {
        if (open) {
            if (isMobile.value && props.isInDialog) {
                isMenuOpen.value = true;
            }
            nextTick(() => {
                autocompleteRef.value?.focus?.();
                setTimeout(() => {
                    const input = autocompleteRef.value?.$el?.querySelector('input');
                    if (input) {
                        input.focus();
                        input.select();
                    }
                }, 50); 
            });
        } else {
            if (isMobile.value && props.isInDialog) {
                isMenuOpen.value = false;
            }
        }
    },
);

const { t } = useI18n();
</script>
<template>
    <!-- :autofocus="isMobile" -->
    <v-autocomplete ref="autocompleteRef" :menu="isMenuOpen" :menu-props="menuProps" v-model="selectedLocation"
        :clearable="!!selectedLocation" clear-icon="mdi-close" :label="label || t('common.select_location')" :items="airports"
        item-title="city" item-value="code" return-object class="fs-location-autocomplete"
        autocomplete="off" :custom-filter="customFilter" @update:model-value="onLocationSelect" @update:menu="onMenuUpdate">
        <template v-slot:item="{ props, item }">
            <!-- Current Location Section Header -->
            <template v-if="item.raw.isCurrent && currentLocation.indexOf(item.raw) === 0">
                <v-list-item class="fs-section-header" disabled>
                    <v-list-item-title class="fs-section-title">
                        <v-icon icon="mdi-map-marker" color="primary"></v-icon>
                        {{ t('locations.current_location') || 'Current Location' }}
                    </v-list-item-title>
                </v-list-item>
            </template>

            <!-- Current Location Item -->
            <v-list-item v-if="item.raw.isCurrent" v-bind="props" :title="undefined" class="fs-location-item">
                <template v-slot:prepend>
                    <v-icon icon="mdi-map-marker" color="primary" size="20"></v-icon>
                </template>
                <v-list-item-title class="fs-location-title"> {{ item.raw.city }}, {{ item.raw.country }}
                </v-list-item-title>
                <v-list-item-subtitle class="fs-location-subtitle">
                    {{ item.raw.address }}
                </v-list-item-subtitle>
                <template v-slot:append>
                    <div class="fs-location-actions">
                        <v-icon icon="mdi-airplane" color="grey-darken-1"></v-icon>
                        <v-chip color="primary" size="small" class="fs-airport-code">
                            {{ item.raw.code }}
                        </v-chip>
                    </div>
                </template>
            </v-list-item>

            <!-- All Locations Section Header -->
            <template v-if="!item.raw.isCurrent && allLocations.indexOf(item.raw) === 0">
                <v-list-item class="fs-section-header" disabled>
                    <v-list-item-title class="fs-section-title">
                        <v-icon icon="mdi-earth" color="grey-darken-1"></v-icon>
                        {{ t('locations.all_locations') }}
                    </v-list-item-title>
                </v-list-item>
            </template>

            <!-- All Locations Item -->
            <v-list-item v-if="!item.raw.isCurrent" v-bind="props" :title="undefined" class="fs-location-item">
                <template v-slot:prepend>
                    <v-icon icon="mdi-earth" color="grey-darken-1" size="20"></v-icon>
                </template>
                <v-list-item-title class="fs-location-title"> {{ item.raw.city }}, {{ item.raw.country }}
                </v-list-item-title>
                <v-list-item-subtitle class="fs-location-subtitle">
                    {{ item.raw.address }}
                </v-list-item-subtitle>
                <template v-slot:append>
                    <div class="fs-location-actions">
                        <v-icon icon="mdi-airplane" color="grey-darken-1"></v-icon>
                        <v-chip color="primary" size="small" class="fs-airport-code">
                            {{ item.raw.code }}
                        </v-chip>
                    </div>
                </template>
            </v-list-item>
        </template>
    </v-autocomplete>
</template>
